import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Wrench, RotateCcw } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLE = {
  pending: "border-amber-700 text-amber-400",
  confirmed: "border-green-700 text-green-400",
  in_progress: "border-sky-700 text-sky-400",
  completed: "border-zinc-600 text-zinc-300",
  cancelled: "border-red-800 text-red-400",
  no_show: "border-red-800 text-red-400",
};

export const BookingRow = ({ b }) => {
  const nav = useNavigate();
  return (
    <div data-testid={`customer-booking-${b.id}`} className="bg-zinc-950 p-5 flex flex-wrap items-start gap-4">
      <div className="min-w-[130px]">
        <div className="font-mono text-xs text-zinc-300">{b.preferred_date}</div>
        <div className="font-mono text-[10px] text-zinc-500">{b.preferred_time}</div>
      </div>
      <div className="flex-1 min-w-[220px]">
        <div className="text-white font-semibold text-sm">{b.service_title}</div>
        <div className="text-xs text-zinc-500">{b.vehicle}</div>
        {(b.work_done || b.services_performed?.length > 0) && (
          <div className="mt-2 text-xs text-zinc-400 flex items-start gap-2">
            <Wrench className="h-3.5 w-3.5 text-brand-red shrink-0 mt-0.5" />
            <span>
              {b.services_performed?.length > 0 && <span className="text-zinc-300">{b.services_performed.join(", ")}</span>}
              {b.services_performed?.length > 0 && b.work_done && " — "}
              {b.work_done}
              {b.price_charged != null && <span className="text-zinc-300 font-semibold"> — ${Number(b.price_charged).toFixed(2)}</span>}
            </span>
          </div>
        )}
        {b.odometer_km != null && (
          <div className="mt-1 text-[11px] text-zinc-500">Odometer: <span className="text-zinc-300">{Number(b.odometer_km).toLocaleString()} km</span></div>
        )}
        {b.status === "completed" && b.next_service_due && (
          <div className="mt-1 text-[11px] text-zinc-500">
            Next service due: <span className="text-zinc-300">{b.next_service_due}</span>
            {b.next_service_km != null && <> or at <span className="text-zinc-300">{Number(b.next_service_km).toLocaleString()} km</span></>}
          </div>
        )}
        {b.job_photos?.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2" data-testid={`booking-photos-${b.id}`}>
            {b.job_photos.map((p) => (
              <a key={p.id} href={`${BASE}${p.url}`} target="_blank" rel="noreferrer" className="relative block group" title={`View ${p.label} photo full size`}>
                <img src={`${BASE}${p.url}`} alt={`${p.label} photo`} loading="lazy"
                  className="h-16 w-16 object-cover border border-zinc-800 group-hover:border-brand-red transition-colors" />
                <span className="absolute bottom-0 left-0 px-1 py-0.5 bg-black/80 text-[8px] font-mono uppercase tracking-widest text-zinc-300">{p.label}</span>
              </a>
            ))}
          </div>
        )}
      </div>
      <div className="flex flex-col items-end gap-2">
        <span className={`px-2.5 py-1 border text-[10px] font-mono uppercase tracking-widest ${STATUS_STYLE[b.status] || "border-zinc-700 text-zinc-400"}`}>
          {(b.status || "").replace("_", " ")}
        </span>
        {["pending", "confirmed"].includes(b.status) && b.manage_token && (
          <Link to={`/booking/manage/${b.manage_token}`} className="text-[11px] text-brand-red hover:underline">
            Change / cancel
          </Link>
        )}
        {["completed", "cancelled", "no_show"].includes(b.status) && (
          <button
            data-testid={`rebook-btn-${b.id}`}
            onClick={() => nav(`/book?service=${encodeURIComponent(b.service || "")}`, {
              state: { rebook: {
                vehicle_make: b.vehicle_make || "", vehicle_model: b.vehicle_model || "",
                vehicle_year: b.vehicle_year || "", vehicle_rego: b.vehicle_rego || "",
                vehicle_state: b.vehicle_state || "TAS",
              } },
            })}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-zinc-700 text-[11px] font-mono uppercase tracking-widest text-zinc-300 hover:border-brand-red hover:text-brand-red transition-colors"
          >
            <RotateCcw className="h-3 w-3" /> Book again
          </button>
        )}
      </div>
    </div>
  );
};
